import initTypeFilter from './type-filter';

interface BlogResponse {
	html: string;
	pagination: string;
	page: number;
	pages: number;
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initListing(root: HTMLElement): void {
	const grid = root.querySelector<HTMLElement>('[data-blog-grid]');
	const nav = root.querySelector<HTMLElement>('[data-blog-pagination]');
	const empty = root.querySelector<HTMLElement>('[data-blog-empty]');
	const endpoint = root.dataset.endpoint;
	const type = root.dataset.type ?? 'none';

	if (!grid || !nav || !endpoint) {
		return;
	}

	let category = root.dataset.category ?? '';
	let controller: AbortController | null = null;
	let observer: IntersectionObserver | null = null;

	root.classList.add('is-enhanced');

	// Filter and numbered pages are kept in the address, so a shared link opens the same view.
	const syncUrl = (page: number): void => {
		const url = new URL(window.location.href);

		url.searchParams.delete('category');
		url.searchParams.delete('pg');

		if (category) {
			url.searchParams.set('category', category);
		}

		if ('numbers' === type && page > 1) {
			url.searchParams.set('pg', String(page));
		}

		window.history.replaceState(window.history.state, '', url);
	};

	const scrollToTop = (): void => {
		const header = document.querySelector<HTMLElement>('[data-armada-header]');
		const top = root.getBoundingClientRect().top + window.scrollY - (header?.offsetHeight ?? 0);

		window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
	};

	const watchMore = (): void => {
		observer?.disconnect();

		const more = nav.querySelector<HTMLElement>('[data-blog-more]');

		if ('infinite' !== type || !more) {
			return;
		}

		more.setAttribute('tabindex', '-1');
		more.setAttribute('aria-hidden', 'true');

		observer ??= new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting) && !controller) {
					const next = nav.querySelector<HTMLElement>('[data-blog-more]');

					if (next) {
						void load(Number(next.dataset.blogPage), true);
					}
				}
			},
			{ rootMargin: '0px 0px 400px 0px' }
		);
		observer.observe(more);
	};

	const load = async (page: number, append: boolean, focus = false): Promise<void> => {
		controller?.abort();
		controller = new AbortController();

		const params = new URLSearchParams({
			category,
			page: String(page),
			per_page: root.dataset.perPage ?? '0',
			type: 'none' === type ? 'more' : type,
			more: root.dataset.more ?? '',
			base: root.dataset.base ?? '',
		});

		(root.dataset.exclude ?? '')
			.split(',')
			.filter(Boolean)
			.forEach((id) => params.append('exclude[]', id));

		root.classList.add('is-loading');
		grid.setAttribute('aria-busy', 'true');

		try {
			const response = await fetch(`${endpoint}?${params.toString()}`, {
				signal: controller.signal,
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				throw new Error(String(response.status));
			}

			const data = (await response.json()) as BlogResponse;

			if (append) {
				grid.insertAdjacentHTML('beforeend', data.html);
			} else {
				grid.innerHTML = data.html;
			}

			nav.innerHTML = data.pagination;

			if (empty) {
				empty.hidden = '' !== grid.innerHTML.trim();
			}

			root.dataset.page = String(data.page);
			syncUrl(data.page);
			controller = null;
			watchMore();

			if (focus) {
				grid.focus({ preventScroll: true });
			}
		} catch (error) {
			if ((error as Error).name === 'AbortError') {
				return;
			}

			controller = null;

			// The links still work without the script, so fall back to them.
			const fallback = new URL(root.dataset.base ?? window.location.href);

			if (category) {
				fallback.searchParams.set('category', category);
			}

			if (page > 1) {
				fallback.searchParams.set('pg', String(page));
			}

			window.location.href = fallback.toString();
		} finally {
			if (!controller) {
				root.classList.remove('is-loading');
				grid.removeAttribute('aria-busy');
			}
		}
	};

	const filter = initTypeFilter(root, (next) => {
		if (next === category && !controller) {
			return;
		}

		category = next;
		root.dataset.category = category;
		filter?.set(category);
		void load(1, false);
	});

	nav.addEventListener('click', (event) => {
		const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[data-blog-page]');

		if (!link) {
			return;
		}

		event.preventDefault();

		if ('numbers' === type) {
			scrollToTop();
			void load(Number(link.dataset.blogPage), false, true);
		} else {
			void load(Number(link.dataset.blogPage), true);
		}
	});

	watchMore();
}

export default function initBlog(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-blog]').forEach(initListing);
}
